import React from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import SignedImage from "./SignedImage";
import useSignedUrl from "../../utils/useSignedUrl";
import { storyRouteLinkProps } from "./ctaTargets";
import { COLORS } from "../../theme/tokens";

const ICON_COLORS = {
  orange: COLORS.orange,
  blue: COLORS.blue,
  red: COLORS.red,
  lightblue: COLORS.lightBlue,
};

function CompactCard({ block }) {
  const c = block.content || {};
  const accent = ICON_COLORS[c.iconColor] || COLORS.blue;
  const ctaTarget = c.sourceRoute && (!c.ctaRoute || c.ctaRoute === "#ouders") ? c.sourceRoute : c.ctaRoute;

  return (
    <Stack
      spacing={1.5}
      sx={{
        height: "100%",
        backgroundColor: "#fff",
        borderRadius: 1,
        p: 2.75,
        border: "1px solid rgba(32, 48, 51, 0.14)",
        boxShadow: "0 16px 36px rgba(32, 48, 51, 0.08)",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          backgroundColor: accent,
        }}
      />
      {c.s3Key && (
        <Box sx={{ aspectRatio: "16 / 9", borderRadius: 2, overflow: "hidden" }}>
          <SignedImage s3Key={c.s3Key} alt={c.imageAlt || c.heading} />
        </Box>
      )}
      <Typography
        variant="h5"
        component="h3"
        sx={{ fontFamily: "windroos, sans-serif", color: COLORS.blue, m: 0 }}
      >
        {c.heading}
      </Typography>
      <Typography variant="body2" sx={{ color: COLORS.muted }}>
        {c.body}
      </Typography>
      {c.ctaLabel && ctaTarget && (
        <Box sx={{ mt: "auto", pt: 0.5 }}>
          <Box
            component={Link}
            {...storyRouteLinkProps(ctaTarget)}
            sx={{
              color: COLORS.red,
              fontWeight: 600,
              textDecoration: "none",
              fontSize: "0.92rem",
              minHeight: 24,
              py: 0.5,
              display: "inline-flex",
              alignItems: "center",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {c.ctaLabel} →
          </Box>
        </Box>
      )}
    </Stack>
  );
}

function DocumentStripLink({ to, href, external, children }) {
  const linkProps = href
    ? {
        component: "a",
        href,
        target: external ? "_blank" : undefined,
        rel: external ? "noopener noreferrer" : undefined,
      }
    : { component: Link, ...storyRouteLinkProps(to) };
  return (
    <Box
      {...linkProps}
      sx={{
        minHeight: 58,
        display: "grid",
        placeItems: "center",
        textAlign: "center",
        padding: "12px",
        borderRadius: 1,
        backgroundColor: COLORS.paper,
        border: "1px solid rgba(32, 48, 51, 0.14)",
        color: COLORS.red,
        fontWeight: 800,
        textDecoration: "none",
        boxShadow: "0 10px 28px rgba(32, 48, 51, 0.06)",
        "&:hover": {
          backgroundColor: "#fff",
          borderColor: "rgba(209, 77, 24, 0.32)",
        },
      }}
    >
      {children}
    </Box>
  );
}

function useSchoolgidsUrl(documentBlocks) {
  const schoolgidsBlock = documentBlocks.find((block) => block.content?.s3Key);
  const s3Key = schoolgidsBlock?.content?.s3Key;
  const { url } = useSignedUrl(s3Key);
  return url;
}

function ParentsDocumentStrip({ documentBlocks }) {
  const schoolgidsUrl = useSchoolgidsUrl(documentBlocks);
  // Label must match the actual document on /documenten ("Ondersteuningsprofiel"),
  // not the legacy "Ondersteuningsplan" name (review 2026-05-18 #7).
  const actions = [
    {
      label: "Schoolgids",
      href: schoolgidsUrl,
      to: schoolgidsUrl ? undefined : "/documenten",
      external: true,
    },
    { label: "Ondersteuningsprofiel", to: "/documenten" },
    { label: "Informatie & documenten", to: "/documenten" },
    { label: "Jaarkalender", to: "/kalender" },
  ];

  return (
    <Box
      sx={{
        mt: "26px",
        display: "grid",
        gap: "12px",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
      }}
    >
      {actions.map((action) => (
        <DocumentStripLink
          key={action.label}
          to={action.to}
          href={action.href}
          external={action.external}
        >
          {action.label}
        </DocumentStripLink>
      ))}
    </Box>
  );
}

export default function CompactCardsSection({ section }) {
  const { content, blocks = [] } = section;
  const sorted = [...blocks].sort((a, b) => a.order - b.order);
  const compactCards = sorted.filter((b) => b.blockType === "text-card");
  const documentBlocks = sorted.filter((b) => b.blockType === "cta");

  return (
    <Box
      id="ouders"
      component="section"
      sx={{
        py: "clamp(48px, 7vw, 92px)",
        px: "clamp(20px, 5vw, 72px)",
        backgroundColor: "#fff",
        scrollMarginTop: { xs: 64, md: 80 },
      }}
    >
      <Container disableGutters sx={{ maxWidth: { md: 1160 } }} maxWidth={false}>
        <SectionHeader
          eyebrow={content.eyebrow}
          heading={content.heading}
          lead={content.lead}
          reviewStatus="controleer"
        />
        <Box
          sx={{
            mt: 4,
            display: "grid",
            gap: { xs: 2.5, md: "22px" },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          {compactCards.map((block) => (
            <CompactCard key={block.id} block={block} />
          ))}
        </Box>

        <ParentsDocumentStrip documentBlocks={documentBlocks} />
      </Container>
    </Box>
  );
}
